import { IKioskSessionResponse } from 'types/kiosk/IKioskSessionResponse';
import { TKioskAuthenticationMode } from 'types/kiosk/TKioskAuthenticationMode';
import { TKioskSession } from 'types/kiosk/TKioskSession';
import { fromKioskLanguageId } from 'utils/kiosk-language';

const AUTHENTICATION_MODES: TKioskAuthenticationMode[] = ['OpenList', 'Pin', 'PinPlusPersonalDeviceApproval'];

/** Missing/unrecognised (e.g. a pre-upgrade response) falls back to the old no-gate behaviour. */
const normaliseAuthenticationMode = (mode?: string | null): TKioskAuthenticationMode =>
  AUTHENTICATION_MODES.includes(mode as TKioskAuthenticationMode) ? (mode as TKioskAuthenticationMode) : 'OpenList';

const normaliseId = (id?: number | null): number | null => {
  if (id === undefined || id === null || Number.isNaN(id) || id === 0) {
    return null;
  }
  return id;
};

const normaliseString = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }
  return value;
};

/**
 * Maps a raw kiosk response to the app's session shape, applying the mobile parity rules:
 * - reject (return null) when `sessionReference` is empty or `locationId` is missing/0;
 * - `costCentreId` 0 / NaN / missing -> null;
 * - empty-string `costCentreName` / `name` -> null.
 */
export const fromResponseToKioskSession = (
  response: IKioskSessionResponse | null | undefined,
  fallbackDeviceIdentifier: string,
): TKioskSession | null => {
  if (!response?.sessionReference || !response?.locationId) {
    return null;
  }

  return {
    sessionReference: response.sessionReference,
    locationId: response.locationId,
    locationName: response.locationName ?? '',
    costCentreId: normaliseId(response.costCentreId),
    costCentreName: normaliseString(response.costCentreName),
    name: normaliseString(response.name),
    deviceIdentifier: response.deviceIdentifier || fallbackDeviceIdentifier,
    authenticationMode: normaliseAuthenticationMode(response.authenticationMode),
    language: fromKioskLanguageId(response.languageId),
  };
};
